// FormSyncService.js

/**
 * FormSyncService.js
 *
 * Keeps form templates aligned with node type schema changes.
 */

import { collection, getDocs, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

import {
  appendMissingDefaultFormFields,
  getVisibleFormFieldKeys,
  pruneFormFieldKeys,
} from 'services/forms/FormModel';
import { updateForm } from 'services/forms/FormWriteService';

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

async function fetchFormsForType({ baseKey, typeKey }) {
  const cleanedBaseKey = normalizeString(baseKey);
  const cleanedTypeKey = normalizeString(typeKey);

  if (!cleanedBaseKey || !cleanedTypeKey) {
    return [];
  }

  const q = query(
    collection(db, collections.forms),
    where('baseKey', '==', cleanedBaseKey),
    where('typeKey', '==', cleanedTypeKey),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() || {}),
    key: docSnap.data()?.key || docSnap.id,
  }));
}

/**
 * Syncs all form templates for a type after schema field keys change.
 *
 * - Removes deleted schema fields from every form.
 * - Appends new visible fields to the default Details form.
 *
 * @param {object} params
 * @param {string} params.baseKey
 * @param {string} params.typeKey
 * @param {object|null} params.typeDef - Type definition with updated schema.fields.
 * @param {string} [params.userKey]
 */
export async function syncFormsWithTypeSchema({
  baseKey,
  typeKey,
  typeDef = null,
  userKey = '',
} = {}) {
  const cleanedBaseKey = normalizeString(baseKey);
  const cleanedTypeKey = normalizeString(typeKey);

  if (!cleanedBaseKey || !cleanedTypeKey || !typeDef) {
    return;
  }

  const allowedFieldKeys = getVisibleFormFieldKeys(typeDef);
  const forms = await fetchFormsForType({
    typeKey: cleanedTypeKey,
    baseKey: cleanedBaseKey,
  });

  if (!forms.length) {
    return;
  }

  await Promise.all(forms.map(async (formDoc) => {
    let nextDoc = formDoc;
    const pruned = pruneFormFieldKeys(formDoc, allowedFieldKeys);

    if (pruned) {
      nextDoc = pruned;
    }

    if (nextDoc?.isDefault) {
      const appended = appendMissingDefaultFormFields(nextDoc, typeDef);
      if (appended) {
        nextDoc = appended;
      }
    }

    if (nextDoc === formDoc) {
      return;
    }

    await updateForm(nextDoc.key, {
      blocksByKey: nextDoc.blocksByKey,
      layout: nextDoc.layout,
      typeKey: cleanedTypeKey,
    }, {
      typeKey: cleanedTypeKey,
      userKey,
      baseKey: cleanedBaseKey,
    });
  }));
}
